import { Anchor, Button, Flex, Group, Stack, Text } from '@mantine/core';
import { IconBrandTelegram, IconBrandWhatsapp, IconMail, IconPhone } from '@tabler/icons-react';
import React from 'react';

import { IContact } from 'entities/Team/types';
import { type ContactLinkResult } from 'hooks/useContactLink';
import { useContacts } from 'hooks/useContacts';
import { useDevices } from 'utils/devices';

import s from './Contacts.module.scss';

type Props = {
  contacts: IContact[];
  gapBetweenContacts?: string;
  isIconVisibleButton?: boolean;
};

const Contacts: React.FC<Props> = ({ contacts, gapBetweenContacts, isIconVisibleButton = true }) => {
  const { isDesktop } = useDevices();
  const { contactsByChannel, links, texts } = useContacts(contacts);

  const { phone: ph, email: em, telegram: tg, whatsapp: wa } = contactsByChannel;
  const { phone: phoneLink, email: emailLink, telegram: telegramLink, whatsapp: whatsappLink } = links;

  const phoneLinkProps = phoneLink ? { href: phoneLink.href, target: phoneLink.target, rel: phoneLink.rel } : null;
  const emailLinkProps = emailLink ? { href: emailLink.href, target: emailLink.target, rel: emailLink.rel } : null;

  const renderContactContent = React.useCallback(
    (icon: React.ReactNode, value: string) => (
      <Group gap="xxxs" wrap="nowrap" align="center" className={s.contactRow}>
        <Text c="indigo" component="span" className={s.contactIcon}>
          {icon}
        </Text>
        <Text fz="xs" c="indigo" className={s.contactText}>
          {value}
        </Text>
      </Group>
    ),
    [],
  );

  const renderButton = React.useCallback(
    ({
      label,
      color,
      icon,
      link,
    }: {
      label: string;
      color: string;
      icon: React.ReactNode;
      link?: ContactLinkResult | null;
    }) => (
      <Button
        variant="light"
        h="30"
        w="100%"
        radius="sm"
        size="xs"
        fw="400"
        fz="xs"
        bd="0"
        color={color}
        leftSection={isIconVisibleButton && isDesktop ? icon : null}
        component={link ? 'a' : undefined}
        href={link?.href}
        target={link?.target}
        rel={link?.rel}
      >
        {label}
      </Button>
    ),
    [isDesktop, isIconVisibleButton],
  );

  const { phone: phoneText, email: emailText } = texts;

  if (contacts.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap={gapBetweenContacts || 'md'}>
      {(tg || wa) && (
        <Group gap={8} grow w="100%">
          {tg &&
            renderButton({
              label: 'Telegram',
              color: 'indigo',
              icon: <IconBrandTelegram size={12} />,
              link: telegramLink ?? null,
            })}

          {wa &&
            renderButton({
              label: 'WhatsApp',
              color: 'green',
              icon: <IconBrandWhatsapp size={12} />,
              link: whatsappLink ?? null,
            })}
        </Group>
      )}

      <Stack gap="xxs">
        {ph &&
          (phoneLinkProps ? (
            <Anchor className={s.link} {...phoneLinkProps}>
              {renderContactContent(<IconPhone size={16} />, phoneText)}
            </Anchor>
          ) : (
            renderContactContent(<IconPhone size={16} />, phoneText)
          ))}

        {em &&
          (emailLinkProps ? (
            <Anchor className={s.link} {...emailLinkProps}>
              {renderContactContent(<IconMail size={16} />, emailText)}
            </Anchor>
          ) : (
            renderContactContent(<IconMail size={16} />, emailText)
          ))}
      </Stack>
    </Flex>
  );
};

export default Contacts;
